import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { NotifyProvider } from "@/lib/notify";
import Layout from "@/components/Layout";
import BrandTitle from "@/components/BrandTitle";
import LoginPage from "@/pages/LoginPage";
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const AdvertiserPage = lazy(() => import("@/pages/AdvertiserPage"));
const DataDonasiPage = lazy(() => import("@/pages/DataDonasiPage"));
const FinancePage = lazy(() => import("@/pages/FinancePage"));
const ProgramsPage = lazy(() => import("@/pages/ProgramsPage"));
const AkunIklanPage = lazy(() => import("@/pages/AkunIklanPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const KontenIklanPage = lazy(() => import("@/pages/KontenIklanPage"));
const InterestPage = lazy(() => import("@/pages/InterestPage"));
const KampanyePage = lazy(() => import("@/pages/KampanyePage"));
const BankTransferPage = lazy(() => import("@/pages/BankTransferPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const BukuKasPage = lazy(() => import("@/pages/BukuKasPage"));
import { Loader2 } from "lucide-react";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-emerald-600" size={32} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return (
    <Layout>
      <Suspense fallback={<div className="py-24 flex justify-center"><Loader2 className="animate-spin text-emerald-600" size={28} /></div>}>
        {children}
      </Suspense>
    </Layout>
  );
}

function RouterInner() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
      <Route path="/laporan-advertiser" element={<Protected roles={["admin", "advertiser"]}><AdvertiserPage /></Protected>} />
      <Route path="/data-donasi" element={<Protected roles={["admin", "cs", "finance", "advertiser"]}><DataDonasiPage /></Protected>} />
      {/* Laporan CS dihapus — semua donasi kini dikelola di Data Donasi */}
      <Route path="/laporan-cs" element={<Navigate to="/data-donasi" replace />} />
      <Route path="/finance" element={<Protected roles={["admin", "finance"]}><FinancePage /></Protected>} />
      <Route path="/buku-kas" element={<Protected roles={["admin", "finance"]}><BukuKasPage /></Protected>} />
      <Route path="/konten-iklan" element={<Protected roles={["admin", "advertiser"]}><KontenIklanPage /></Protected>} />
      <Route path="/kelola-interest" element={<Protected roles={["admin", "advertiser"]}><InterestPage /></Protected>} />
      <Route path="/kampanye" element={<Protected roles={["admin", "advertiser"]}><KampanyePage /></Protected>} />
      <Route path="/bank-transfer" element={<Protected roles={["admin", "advertiser", "finance"]}><BankTransferPage /></Protected>} />
      {/* Menu Save Nomor dihapus — link lama diarahkan ke Dashboard */}
      <Route path="/save-nomor" element={<Navigate to="/dashboard" replace />} />
      <Route path="/manajemen-program" element={<Protected roles={["admin", "advertiser"]}><ProgramsPage /></Protected>} />
      <Route path="/manajemen-akun-iklan" element={<Protected roles={["admin", "advertiser"]}><AkunIklanPage /></Protected>} />
      <Route path="/manajemen-user" element={<Protected roles={["admin"]}><UsersPage /></Protected>} />
      <Route path="/profil" element={<Protected><ProfilePage /></Protected>} />
      <Route path="/pengaturan" element={<Protected roles={["admin"]}><SettingsPage /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <NotifyProvider>
            <BrandTitle />
            <RouterInner />
            <Toaster richColors position="top-right" />
          </NotifyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
